<!-- スケジュール概要表示コンポーネント -->
<template>
  <div class="schedule-summary">
    <b-row no-gutters class="summary">
      <div class="summary-header">
        <p v-if="assignNotificationText" class="register-notification">
          {{ assignNotificationText }}
        </p>
        <p class="register-info">
          {{
            $t('appScheduleSummary.registerInfo', {
              date: createAt,
              name: createUserName,
            })
          }}
        </p>
      </div>
      <h4 class="schedule-title">
        {{ displayTitle }}
      </h4>
      <p class="schedule-period">
        {{ displayPeriod }}
      </p>

      <div v-if="hasMultiTargetUsers" class="summary-item">
        <span class="title">
          {{ $tc('appScheduleSummary.targetUsers', targetUserCount || 1) }}
        </span>
        <div class="target-user-names">
          <span
            v-for="(d, index) in targets"
            v-bind:key="`${d.type}_${d.id}`"
            class="name"
          >
            <!-- 組織 -->
            <text-organization
              v-if="d.type === 'organization'"
              v-bind="d.data"
            />
            <!-- ユーザー -->
            <span v-else v-text="d.name" />
            <!-- カンマ -->
            <span
              v-if="index !== targets.length - 1"
              class="trailing-comma"
              v-text="','"
            />
          </span>
        </div>
      </div>

      <div v-if="hasMultiTargetUsers" class="summary-item">
        <span class="title">
          {{ $tc('appScheduleSummary.assignees', assignedUserCount || 1) }}
        </span>
        <!-- 担当者が決まっている -->
        <p v-if="schedule.hasAssignedUser" class="assign-user-names">
          <span
            v-for="(name, index) in assignedUserNames"
            v-bind:key="name"
            class="user-name"
            v-bind:class="{ last: index === assignedUserNames.length - 1 }"
          >
            <span class="name" v-text="name" />
            <span
              v-if="index !== assignedUserNames.length - 1"
              class="trailing-comma"
              v-text="','"
            />
          </span>
          <span
            v-if="showAssignedUserChangeLabel"
            class="change-assign-user-link"
            v-on:click="$_onClickAssignUserEdit"
            v-text="changeAssignUserLabel"
          />
        </p>
        <!-- 担当者が決まっていない -->
        <p v-else class="assign-user-names undecided">
          <span class="undecided-assign-user-badge">
            <span class="text" v-t="'appScheduleSummary.undecided'" />
          </span>
          <span
            v-if="showAssignedUserChangeLabel"
            class="change-assign-user-link"
            v-on:click="$_onClickAssignUserEdit"
            v-text="changeAssignUserLabel"
          />
        </p>
      </div>
    </b-row>
  </div>
</template>

<script>
import Schedule from '@/model/Schedule.js'
import SchedulePeriodFormatter from '@/js/SchedulePeriodFormatter.js'
import moment from 'moment'

export default {
  name: 'AppScheduleSummary',
  props: {
    /**
     * スケジュールデータ
     */
    schedule: {
      type: Schedule,
      required: true,
    },
    /**
     * メインとするユーザー
     * null の場合はログインユーザーで補完される
     */
    mainUserId: {
      type: Number,
      default: null,
    },
    /**
     * 担当変更モーダルを表示可能か
     */
    showAssignedUserChangeLabel: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * メインユーザーが担当者の場合はtrue
     * @type {Boolean}
     */
    isMainUserAssigned: function () {
      const userId = this.mainUserId || this.$store.state.login.userId
      return this.schedule.isAssigned(userId)
    },
    /**
     * ユーザーへの担当者通知文字列
     * @type {String}
     */
    assignNotificationText: function () {
      if (!this.showAssignedUserChangeLabel) return null
      if (!this.schedule.assignedUsers.length) return null

      if (!this.mainUserId) {
        return (
          this.isMainUserAssigned &&
          this.$tc(
            'appScheduleSummary.message.assignedYou',
            this.schedule.assignedUsers.length - 1
          )
        )
      } else {
        const targetOfMain = this.schedule.targetUsers.find(
          (user) => user.user_id === this.mainUserId
        )
        if (!targetOfMain) return null

        return (
          this.isMainUserAssigned &&
          this.$tc(
            'appScheduleSummary.message.assignedOthers',
            this.schedule.assignedUsers.length - 1,
            { name: targetOfMain.user_name }
          )
        )
      }
    },
    /**
     * 表示用タイトル
     * @type {String}
     */
    displayTitle: function () {
      return this.schedule.title || ''
    },
    /**
     * 表示用期間
     * @type {String}
     */
    displayPeriod: function () {
      return this.formatter.format(this.schedule)
    },
    /**
     * 対象ユーザーが複数人存在するか？
     * @type {Boolean}
     */
    hasMultiTargetUsers: function () {
      return this.schedule && this.schedule.hasMultiTargetUsers
    },
    /**
     * スケジュール登録日時
     * @type {String}
     */
    createAt: function () {
      return (
        this.schedule &&
        moment(this.schedule.createAt).format('YYYY/MM/DD HH:mm')
      )
    },
    /**
     * スケジュール登録者名
     * @type {String}
     */
    createUserName: function () {
      return this.schedule && this.schedule.createUserName
    },
    /**
     * 対象ユーザー数
     * @type {Number}
     */
    targetUserCount: function () {
      return this.schedule && this.schedule.targetUsers
        ? this.schedule.targetUsers.length
        : 0
    },
    /**
     * すべての対象ユーザー/組織
     * @type {Object[]}
     */
    targets: function () {
      return [
        ...(this.schedule?.targetOrganizations || []).map((o) => ({
          type: 'organization',
          id: o.organization_id,
          data: {
            name: o.organization_name,
            users: o.organization_user_relations,
            userNameField: 'user_name',
          },
        })),
        ...(this.schedule?.targetUsers || []).map((u) => ({
          type: 'user',
          id: u.user_id,
          name: u.user_name,
        })),
      ]
    },
    /**
     * 担当者変更アクションラベル
     * @type {String}
     */
    changeAssignUserLabel: function () {
      return this.$store.state.login.isManager
        ? this.$t('appScheduleSummary.action.changeAssignees')
        : this.schedule.isAssigned(this.$store.state.login.userId)
          ? this.$t('appScheduleSummary.action.unassignMe')
          : this.$t('appScheduleSummary.action.assignMe')
    },
    /**
     * 担当者数
     * @type {Number}
     */
    assignedUserCount: function () {
      return this.schedule && Array.isArray(this.schedule.assignedUsers)
        ? this.schedule.assignedUsers.length
        : 0
    },
    /**
     * 全担当者名
     * @type {Array.<String>}
     */
    assignedUserNames: function () {
      return this.schedule && Array.isArray(this.schedule.assignedUsers)
        ? this.schedule.assignedUsers.map((data) => data.user_name)
        : []
    },
  },
  created: function () {
    this.formatter = new SchedulePeriodFormatter({
      allDayText: this.$t('appScheduleSummary.allDay'),
    })
  },
  methods: {
    /**
     * 担当者選択モーダル表示
     */
    $_onClickAssignUserEdit: function () {
      /**
       * 担当者変更ボタンクリック時イベント
       */
      this.$emit('change-assign-user-click')
    },
  },
}
</script>

<style lang="scss" scoped>
.schedule-summary {
  line-height: 1.7;
}

.summary {
  background-color: $entity-color-white;
  padding: 0 20px 20px 20px;
  flex-direction: column;
  text-align: left;
  border: 2px solid $dialog-background-color;
  font-size: 14px;
  color: #212529;

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    height: 40px;
    font-size: 12px;
    line-height: 1;
    .register-info {
      color: #959595;
      margin-top: 20px;
      margin-bottom: 0;
      width: 100%;
      text-align: right;
    }
    .register-notification {
      color: $entity-color-white;
      background-color: #28a744;
      padding: 4px 8px;
      margin-bottom: 0;
      min-width: 88px;
      flex-shrink: 0;
    }
  }

  .schedule-title {
    font-size: $font-size-xlarge;
    font-weight: $font-weight-bold;
    color: $text-color;
  }

  .schedule-period {
    font-size: $font-size-large;
    margin: 0;
    color: $text-color;
    font-weight: $font-weight-bold;
  }

  .summary-item {
    display: flex;

    .title {
      flex-shrink: 0;
    }

    .target-user-names {
      display: block;
      margin: 0;

      .name {
        display: inline-block;

        .trailing-comma {
          margin-right: calc(#{$font-size-middle} / 4); // 半角スペースの代わり
        }
      }
    }

    .assign-user-names {
      display: block;
      margin: 0;

      &.undecided {
        align-items: center;
        column-gap: 1rem;
      }

      .user-name {
        .trailing-comma {
          margin-right: calc(#{$font-size-middle} / 4); // 半角スペースの代わり
        }

        &.last {
          margin-right: 1rem;
        }
      }
    }
  }

  .undecided-assign-user-badge {
    background-color: #c62e4d;
    margin-right: 1rem;
    padding: 1px 8px 0 8px;
    border-radius: $font-size-small;
    display: inline-flex;
    justify-content: center;
    align-items: center;

    .text {
      color: $entity-color-white;
      font-size: $font-size-small;
    }
  }

  .change-assign-user-link {
    font-size: $font-size-middle;
    color: $text-color-link;
    text-decoration: underline;
    white-space: nowrap;
    cursor: pointer;
    flex-shrink: 0;
  }
}
</style>
